import { useEffect, useState } from "react";
import { SettingsAPI, AuthAPI, ProfileAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FloppyDisk, Buildings, Bank, Receipt, Key, UserCircle, Car } from "@phosphor-icons/react";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, overline, children }) => (
  <section className="bg-white border border-border p-6 lg:p-8 mb-px">
    <div className="flex items-start gap-3 pb-5 mb-5 border-b border-border">
      <div className="bg-primary/10 text-primary p-2 rounded-sm">
        <Icon size={18} weight="bold" />
      </div>
      <div>
        <div className="overline text-primary">{overline}</div>
        <h2 className="font-display text-xl font-bold tracking-tight mt-1">{title}</h2>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </section>
);

const Field = ({ id, label, value, onChange, type = "text", placeholder, full, testid }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label htmlFor={id} className="overline">
      {label}
    </Label>
    <Input
      id={id}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="rounded-sm mt-1.5"
      data-testid={testid}
    />
  </div>
);

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => {
    ProfileAPI.get().then(setProfile);
    SettingsAPI.get().then(setSettings);
  }, []);

  const updateProfile = (field) => (value) =>
    setProfile((p) => ({ ...p, [field]: value }));
  const updateSettings = (field) => (value) =>
    setSettings((s) => ({ ...s, [field]: value }));

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await ProfileAPI.update(profile);
      toast.success("Profile saved");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await SettingsAPI.update({
        ...settings,
        default_tax_rate: Number(settings.default_tax_rate || 0),
        mileage_rate: Number(settings.mileage_rate || 0),
        mileage_free_miles_per_day: Number(settings.mileage_free_miles_per_day ?? 100),
      });
      toast.success("HEO defaults saved");
    } catch {
      toast.error("Failed to save defaults");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwdForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwdSaving(true);
    try {
      await AuthAPI.changePassword(pwdForm.current_password, pwdForm.new_password);
      toast.success("Password updated");
      setPwdOpen(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to change password");
    } finally {
      setPwdSaving(false);
    }
  };

  if (!profile || !settings) {
    return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div data-testid="settings-page">
      <PageHeader
        overline="Configuration"
        title="Settings"
        description={
          isAdmin
            ? "Your profile and bank details appear on invoices you issue. HEO defaults set logo, VAT rate, and payment terms."
            : "Your profile and bank details appear on every invoice you issue."
        }
        actions={
          <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="change-pwd-btn">
                <Key size={16} weight="bold" className="mr-2" />
                Change Password
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="change-pwd-dialog">
              <DialogHeader>
                <DialogTitle>Change Password</DialogTitle>
                <DialogDescription>Update your login password.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleChangePwd} className="space-y-4">
                <div>
                  <Label className="overline">Current Password</Label>
                  <Input
                    type="password"
                    value={pwdForm.current_password}
                    onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                    required
                    className="rounded-sm mt-1.5"
                    data-testid="current-pwd-input"
                  />
                </div>
                <div>
                  <Label className="overline">New Password</Label>
                  <Input
                    type="password"
                    value={pwdForm.new_password}
                    onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                    required
                    minLength={6}
                    className="rounded-sm mt-1.5"
                    data-testid="new-pwd-input"
                  />
                </div>
                <div>
                  <Label className="overline">Confirm New Password</Label>
                  <Input
                    type="password"
                    value={pwdForm.confirm}
                    onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                    required
                    className="rounded-sm mt-1.5"
                    data-testid="confirm-pwd-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={pwdSaving} className="rounded-sm" data-testid="save-pwd-btn">
                    {pwdSaving ? "Saving…" : "Update Password"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* My Profile — company details (editable by everyone) */}
      <Section icon={UserCircle} overline="My Details" title="My Profile">
        <Field id="p_company_name" label="Business / Trading Name" value={profile.company_name}
          onChange={updateProfile("company_name")} full placeholder="e.g. HEO IT Engineering" testid="profile-company-name-input" />
        <Field id="p_contact_email" label="Contact Email" type="email" value={profile.contact_email}
          onChange={updateProfile("contact_email")} testid="profile-contact-email-input" />
        <Field id="p_phone" label="Phone" value={profile.phone}
          onChange={updateProfile("phone")} testid="profile-phone-input" />
        <Field id="p_website" label="Website" value={profile.website}
          onChange={updateProfile("website")} testid="profile-website-input" />
        <Field id="p_vat_number" label="VAT Number" value={profile.vat_number}
          onChange={updateProfile("vat_number")} testid="profile-vat-input" />
        <Field id="p_registration" label="Company Reg #" value={profile.company_registration}
          onChange={updateProfile("company_registration")} testid="profile-reg-input" />
        <div className="sm:col-span-2">
          <Label htmlFor="p_address" className="overline">Address</Label>
          <Textarea id="p_address" value={profile.address || ""}
            onChange={(e) => updateProfile("address")(e.target.value)} rows={3}
            placeholder="Street, City, Postcode"
            className="rounded-sm mt-1.5 resize-none" data-testid="profile-address-input" />
        </div>
      </Section>

      {/* Bank details */}
      <Section icon={Bank} overline="Payment" title="My Bank Details">
        <Field id="p_bank_name" label="Bank Name *" value={profile.bank_name}
          onChange={updateProfile("bank_name")} testid="profile-bank-name-input" />
        <Field id="p_account_name" label="Account Name (optional)" value={profile.account_name}
          onChange={updateProfile("account_name")} testid="profile-account-name-input" />
        <Field id="p_account_number" label="Account Number *" value={profile.account_number}
          onChange={updateProfile("account_number")} testid="profile-account-number-input" />
        <Field id="p_sort_code" label="Sort Code *" value={profile.sort_code}
          onChange={updateProfile("sort_code")} testid="profile-sort-code-input" />
        <Field id="p_iban" label="IBAN (optional)" value={profile.iban}
          onChange={updateProfile("iban")} testid="profile-iban-input" />
        <Field id="p_swift_bic" label="SWIFT / BIC (optional)" value={profile.swift_bic}
          onChange={updateProfile("swift_bic")} testid="profile-swift-input" />
      </Section>

      <div className="flex justify-end mt-4 mb-10">
        <Button onClick={handleSaveProfile} disabled={savingProfile} className="rounded-sm" data-testid="save-profile-btn">
          <FloppyDisk size={16} weight="bold" className="mr-2" />
          {savingProfile ? "Saving…" : "Save Profile"}
        </Button>
      </div>

      {/* HEO Defaults — admin only */}
      {isAdmin && (
        <>
          <Section icon={Buildings} overline="HEO" title="HEO Portal Defaults">
            <div className="sm:col-span-2">
              <Label htmlFor="logo_url" className="overline">Logo URL</Label>
              <Input id="logo_url" value={settings.logo_url || ""}
                onChange={(e) => updateSettings("logo_url")(e.target.value)}
                placeholder="/api/static/logo.png or https://…"
                className="rounded-sm mt-1.5" data-testid="logo-url-input" />
              {settings.logo_url && (
                <div className="mt-3 p-3 border border-border bg-muted/30 inline-block">
                  <img
                    src={settings.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : settings.logo_url}
                    alt="Logo preview"
                    className="max-h-16 object-contain"
                    data-testid="logo-preview"
                  />
                </div>
              )}
            </div>
          </Section>
          <Section icon={Receipt} overline="Defaults" title="Invoice Defaults">
            <Field id="default_tax_rate" label="Default VAT Rate (%)" type="number"
              value={settings.default_tax_rate} onChange={updateSettings("default_tax_rate")}
              testid="default-tax-rate-input" />
            <Field id="default_payment_terms" label="Default Payment Terms"
              value={settings.default_payment_terms} onChange={updateSettings("default_payment_terms")}
              placeholder="Net 30" testid="default-payment-terms-input" />
            <Field id="currency" label="Currency Code" value={settings.currency}
              onChange={updateSettings("currency")} placeholder="GBP" testid="currency-input" />
          </Section>
          <Section icon={Car} overline="Mileage" title="Subcontractor Mileage">
            <div className="sm:col-span-2 text-xs text-muted-foreground -mb-2">
              Applies to all subcontractor timesheet invoices. Only miles above the first {" "}
              <span className="font-mono font-semibold text-foreground">
                {Number(settings.mileage_free_miles_per_day ?? 100)}
              </span>{" "}
              miles per day are billable at the rate below.
            </div>
            <Field id="mileage_rate" label="Mileage Rate (£ per mile)" type="number"
              value={settings.mileage_rate ?? 0} onChange={updateSettings("mileage_rate")}
              placeholder="0.00" testid="mileage-rate-input" />
            <Field id="mileage_free_miles_per_day" label="Free Miles per Day (not billable)" type="number"
              value={settings.mileage_free_miles_per_day ?? 100}
              onChange={updateSettings("mileage_free_miles_per_day")}
              placeholder="100" testid="mileage-free-input" />
          </Section>
          <div className="flex justify-end mt-4">
            <Button onClick={handleSaveSettings} disabled={savingSettings} className="rounded-sm" data-testid="save-settings-btn">
              <FloppyDisk size={16} weight="bold" className="mr-2" />
              {savingSettings ? "Saving…" : "Save HEO Defaults"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
